<div class="report-wrapper">
	<!-- Control Bar -->
	<div class="control-bar no-print">
		<div class="left-controls">
			<button
				(click)="handleClose()"
				class="back-btn"
			>
				<i class="pi pi-arrow-left"></i> Back to Dispatch
			</button>
			<h1>Operations Preview</h1>
		</div>
		<button
			(click)="handlePrint()"
			class="print-btn"
			[disabled]="isLoading"
		>
			<i class="pi pi-print"></i>
			{{ isLoading ? 'Loading...' : 'Print Report' }}
		</button>
	</div>

	<!-- Loading Overlay -->
	<div
		class="loading-overlay no-print"
		*ngIf="isLoading"
	>
		<div class="loading-content">
			<i class="pi pi-spin pi-spinner"></i>
			<p>{{ loadingMessage }}</p>
		</div>
	</div>

	<div
		class="report-container"
		*ngIf="{ metrics: metrics$ | async } as data"
	>
		<!-- Header -->
		<div class="header">
			<!-- Logo section -->
			<div class="logo-section">
				<div class="logo-text">
					<span class="logo-icon"><i class="pi pi-truck"></i></span>
					{{ zoneName$ | async }}
				</div>
				<span class="tagline">Operations Report</span>
			</div>
			<!-- Title section -->
			<div class="title-section">
				<h2>Daily Operations Report</h2>
				<p><i class="pi pi-calendar"></i> {{ currentDate | date:'fullDate' }}</p>
			</div>
		</div>

		<!-- KPI Dashboard -->
		<div
			class="kpi-dashboard"
			*ngIf="data.metrics"
		>

			<!-- Financials -->
			<div class="kpi-card">
				<div class="bg-icon"><i class="pi pi-dollar"></i></div>
				<h3><i class="pi pi-chart-line"></i> Financial Performance</h3>
				<div class="kpi-values">
					<div>
						<span class="label">Est. Revenue</span>
						<div class="value">${{ data.metrics.revenue | number:'1.0-0' }}</div>
					</div>
					<div class="text-right">
						<span class="label">Labor Cost</span>
						<div class="value-secondary">${{ data.metrics.laborCost | number:'1.0-0' }}</div>
					</div>
				</div>
				<div class="progress-bar">
					<div
						class="progress-fill"
						[style.width.%]="data.metrics.laborPercentage"
					></div>
				</div>
				<p class="kpi-footer">{{ data.metrics.laborPercentage }}% Labor Cost</p>
			</div>

			<!-- Crew -->
			<div class="kpi-card">
				<div class="bg-icon"><i class="pi pi-users"></i></div>
				<h3><i class="pi pi-users"></i> Crew Availability</h3>
				<div class="kpi-values">
					<div>
						<span class="label">Assigned</span>
						<div class="value">{{ data.metrics.crewAssigned }} <span class="sub-value">/ {{
								data.metrics.totalCrew }}</span></div>
					</div>
					<div class="text-right">
						<span class="label">Status</span>
						<div class="value-highlight">{{ data.metrics.crewUtilization }}%</div>
					</div>
				</div>
				<div class="progress-bar">
					<div
						class="progress-fill"
						[style.width.%]="data.metrics.crewUtilization"
					></div>
				</div>
				<p class="kpi-footer-secondary">Utilization Rate</p>
			</div>

			<!-- Fleet -->
			<div class="kpi-card">
				<div class="bg-icon"><i class="pi pi-truck"></i></div>
				<h3><i class="pi pi-truck"></i> Fleet Status</h3>
				<div class="kpi-values">
					<div>
						<span class="label">Trucks Active</span>
						<div class="value">{{ data.metrics.trucksAssigned }} <span class="sub-value">/ {{
								data.metrics.totalTrucks }}</span></div>
					</div>
					<div class="text-right">
						<span class="label">Status</span>
						<div class="value-highlight">{{ data.metrics.truckUtilization }}%</div>
					</div>
				</div>
				<div class="progress-bar">
					<div
						class="progress-fill"
						[style.width.%]="data.metrics.truckUtilization"
					></div>
				</div>
				<p class="kpi-footer-secondary">Fleet Utilization</p>
			</div>
		</div>

		<!-- Schedule Section -->
		<div class="schedule-section">
			<!-- OPS Section — ops-manager / reserve / admin / ops-task events (Ray: above workorders) -->
			<div
				class="ops-section"
				*ngIf="formattedOpsEvents.length"
			>
				<h3 class="section-title ops-section-title">OPS — Blocks &amp; Tasks</h3>
				<div
					class="ops-item"
					*ngFor="let event of formattedOpsEvents"
				>
					<div class="ops-item-head">
						<i class="pi"
							[ngClass]="event.event.type === 'ops-task' ? 'pi-check-square' : 'pi-flag'"></i>
						<strong>{{ event.event.title || event.event.type | titlecase }}</strong>
						<span class="ops-time">{{ event.startTime }} - {{ event.endTime }}</span>
						<span class="ops-type">{{ event.event.type | titlecase }}</span>
					</div>
					<!-- OPS task notes (Quill delta on the task's metadata) -->
					<div
						class="ops-notes"
						*ngIf="event.event.description"
					>
						{{ event.event.description }}
					</div>
					<!-- Ops notes (internal) pulled from the linked job -->
					<div
						class="ops-notes"
						*ngIf="event.event.job?.metadata?.calculatorOutput__opsNotes"
					>
						<strong>Ops notes:</strong> {{ event.event.job.metadata.calculatorOutput__opsNotes }}
					</div>
					<div
						class="ops-crew"
						*ngIf="event.crew?.length"
					>
						<i class="pi pi-users"></i>
						<span *ngFor="let member of event.crew; let last = last">{{ member.name }}<span
								*ngIf="!last">; </span></span>
					</div>
				</div>
			</div>

			<h3 class="section-title">Route Schedule & Job Details</h3>

			<div class="routes-grid">
				<div
					class="route-card"
					*ngFor="let route of formattedRoutes"
				>
					<div class="route-header">
						<div class="header-left">
							<div class="route-header-title-group">
								<span class="route-name">{{ route.routeName }}</span>
								<span class="truck-badge">{{ route.truckId }}</span>
							</div>
							<span
								class="route-date"
								*ngIf="route.routeDate"
							>
								{{ route.routeDate | date:'MMM d' }}
							</span>
						</div>
						<div class="crew-lead">
							<div class="lead-name">
								<i class="pi pi-phone"></i> Lead: {{ route.crewLead?.name || 'No Lead' }}
							</div>
							<div class="lead-phone">{{ formatPhone(route.crewLead?.phone) }}</div>
						</div>
					</div>

					<div class="jobs-list">
						<div
							class="job-item"
							*ngFor="let job of route.jobs; let last = last"
							[class.border-b]="!last"
						>
							<div class="job-time-col">
								<div class="job-badge">
									<span class="job-type">{{ job.event.type }}</span>
									<span class="job-time">{{ job.startTime }}</span>
								</div>

								<div class="job-badge arrival-window">
									<span class="job-type">Arrival Window</span>
									<span class="job-time">{{ job.formattedArrivalWindow }}</span>
								</div>

								<div class="job-badge">
									<span class="job-type">Duration</span>
									<span class="job-time">{{ job.formattedDuration }}</span>
								</div>

								<!-- Operational Metrics -->
								<div class="operational-metrics">
									<div class="job-badge">
										<span class="job-type">Travel Time</span>
										<span class="job-time">{{ job.formattedTravelTime }}</span>
									</div>
									<div class="job-badge">
										<span class="job-type">Mileage</span>
										<span class="job-time">{{ job.displayMileage | number:'1.2-2' }} {{
											job.distanceUnit }}</span>
									</div>
									<div class="job-badge">
										<span class="job-type">Inv Weight</span>
										<span class="job-time">{{ job.inventoryWeight }}</span>
									</div>
									<div class="job-badge">
										<span class="job-type">Inv Volume</span>
										<span class="job-time">{{ job.inventoryVolume }}</span>
									</div>
								</div>
							</div>
							<div class="job-details-col">
							<div class="customer-info">
								<h4>{{ job.customer.name }}</h4>
								<div class="customer-phone"><i class="pi pi-phone"></i> {{
									formatPhone(job.customer.user?.phone)
									}}</div>
								<!-- Additional contacts (Ray: must show on dispatch / ops report) -->
								<div
									class="customer-contacts"
									*ngIf="job.additionalContacts.length"
								>
									<div *ngFor="let contact of job.additionalContacts">
										<i class="pi pi-user"></i>
										{{ contact.name }}<span *ngIf="contact.relationship"> ({{ contact.relationship }})</span><span
											*ngIf="contact.phone"> · </span>
										<span *ngIf="contact.phone">{{ contact.phone }}</span><span
											*ngIf="contact.email"> · </span>
										<span *ngIf="contact.email">{{ contact.email }}</span>
									</div>
								</div>
							</div>
								<div
									class="location-info"
									*ngIf="job.locationsToShow.length"
								>
									<div
										*ngFor="let loc of job.locationsToShow; let i = index"
										class="location-line"
									>
										<i class="pi pi-map-marker"></i>
										<strong>{{ loc.formattedLabel }}: </strong>
										<span class="location-address">{{ loc.location?.addressLineOne }}</span>
									</div>
								</div>

								<!-- Job Financials -->
								<div class="job-financials">
									<div class="fin-item">
										<span class="label">Rev:</span> ${{ (job.event.discountedSubTotal || 0) / 100 |
										number:'1.0-0' }}
									</div>
									<div class="fin-item">
										<span class="label">Cost:</span> ${{ (job.totalLaborCost || 0) / 100 |
										number:'1.0-0' }}
									</div>
									<div class="fin-item">
										<span class="label">Labor:</span> {{ job.laborPercentage }}%
									</div>
								</div>

								<!-- Notes -->
								<div
									class="notes-info"
									*ngIf="job.event.description"
								>
									Note: {{ job.event.description }}
								</div>

								<!-- Crew Information -->
								<div
									class="crew-list"
									*ngIf="job.crew?.length"
								>
									<i class="pi pi-users"></i>
									({{ job.crew.length || 0 }}/{{ job.crewRequirement || 0 }})
									<span *ngFor="let member of job.crew; let last = last">
										{{ member.name }}<span *ngIf="!last">; </span>
									</span>
								</div>
								<ng-container *ngIf="job.crewSummaryParagraphs.length">
									<div class="crew-list">
										<i class="pi pi-align-left"></i>
										<div class="crew-content">
											<strong>Summary:</strong>
											<div *ngFor="let para of job.crewSummaryParagraphs">{{ para }}</div>
										</div>
									</div>
								</ng-container>

								<div
									class="crew-list"
									*ngIf="job.event.charges?.length"
								>
									<i class="pi pi-receipt"></i>
									<div class="crew-content">
										<strong>Charges:</strong>
										<div *ngFor="let charge of job.event.charges">
											({{ charge.quantity }}) {{ charge.product?.name || '' }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="footer promo-footer">
			<div class="footer-content">
				<span class="powered-by">Powered by</span>
				<span class="brand-name"><i class="pi pi-truck"></i> MOVERIGHT</span>
				<span class="promo-text"> - moving software, simplified.</span>
			</div>
			<div class="contact-info">
				about.moveright.app
			</div>
		</div>
	</div>
</div>